import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../../services/supabase';
import { PageHeader, Card, Table, Td, EmptyRow, Badge, CompanyInit, StatCard } from '../../components/ui';

export default function CompanyAttendance({ companyId }: { companyId: string }) {
  const [records, setRecords] = useState<any[]>([]);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });

  const load = useCallback(async () => {
    setLoading(true);
    const { data: employees } = await sb.from('users').select('id, full_name, employee_code, role, shift:shifts(name, start_time)').eq('company_id', companyId).eq('is_active', true).in('role', ['employee', 'field_executive', 'manager', 'company_admin']);
    const ids = (employees || []).map((u: any) => u.id);
    setAllEmployees(employees || []);
    if (ids.length > 0) {
      const { data } = await sb.from('attendance').select('*, user:users(full_name, employee_code), attendance_logs(*)').in('user_id', ids).eq('date', date).order('check_in_time', { ascending: false });
      setRecords(data || []);
    }
    setLoading(false);
  }, [companyId, date]);

  useEffect(() => { load(); }, [load]);

  const presentIds = new Set(records.map(r => r.user_id));
  const notCheckedIn = allEmployees.filter(e => !presentIds.has(e.id));
  const present = records.filter(r => r.status === 'present').length;
  const late = records.filter(r => r.status === 'late').length;

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Attendance" subtitle="Daily check-in/out overview"
        action={<input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', fontSize: 14, fontFamily: 'var(--font)', outline: 'none' }} />}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total" value={allEmployees.length} color="var(--accent)" />
        <StatCard label="Present" value={present} color="var(--accent)" />
        <StatCard label="Late" value={late} color="var(--warn)" />
        <StatCard label="Not Checked In" value={notCheckedIn.length} color="var(--danger)" />
      </div>
      <Card>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 700 }}>Checked In</div>
        <Table headers={['Employee', 'Check In', 'Check Out', 'Hours', 'Punches', 'Status']}>
          {loading ? <EmptyRow cols={6} message="Loading..." /> :
           records.length === 0 ? <EmptyRow cols={6} message="No check-ins yet for this date" /> :
           records.map(r => {
            const logs = r.attendance_logs || [];
            const firstIn = logs.find((l: any) => l.type === 'check_in');
            const lastOut = [...logs].reverse().find((l: any) => l.type === 'check_out');
            return (
              <tr key={r.id}>
                <Td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><CompanyInit name={r.user?.full_name || '?'} size={32} /><div><div style={{ fontWeight: 600 }}>{r.user?.full_name}</div><div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{r.user?.employee_code}</div></div></div></Td>
                <Td mono>{(firstIn?.timestamp || r.check_in_time) ? new Date(firstIn?.timestamp || r.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</Td>
                <Td mono>{(lastOut?.timestamp || r.check_out_time) ? new Date(lastOut?.timestamp || r.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : <span style={{ color: 'var(--accent)', fontSize: 12 }}>Still in ●</span>}</Td>
                <Td mono><span style={{ color: 'var(--accent)', fontWeight: 700 }}>{r.total_hours || 0}h</span></Td>
                <Td mono>{logs.length || 1}</Td>
                <Td><Badge type={r.status === 'present' ? 'green' : r.status === 'late' ? 'yellow' : 'red'}>{r.status}</Badge></Td>
              </tr>
            );
          })}
        </Table>
      </Card>

      {notCheckedIn.length > 0 && (
        <Card>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 700, color: 'var(--danger)' }}>Not Checked In ({notCheckedIn.length})</div>
          <Table headers={['Employee', 'Code', 'Role', 'Shift']}>
            {notCheckedIn.map(e => (
              <tr key={e.id}>
                <Td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><CompanyInit name={e.full_name || '?'} size={32} /><div style={{ fontWeight: 600 }}>{e.full_name}</div></div></Td>
                <Td mono>{e.employee_code || '—'}</Td>
                <Td><Badge type="purple">{e.role}</Badge></Td>
                <Td>{e.shift?.name ? `🕐 ${e.shift.name} (${e.shift.start_time})` : <span style={{ color: 'var(--muted)' }}>No shift</span>}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
}
